/**
 * Pack Builder Price Delta UI (A/B variant)
 * — Changes Add • $X.XX → Add
 * — Adds Premium / Save labels from the CTA-displayed price vs $12.99 baseline
 * — Ensures premium-priced cups also show the existing "Premium Item" image badge
 *
 * Activates only when .pb[data-price-delta-ui="true"] is present.
 * Source of truth is the dollar amount shown in the Add button text — not product data.
 */
(function () {
    'use strict';

    const EPSILON = 0.005;
    const DEFAULT_BASELINE = 12.99;
    const PREMIUM_ITEM_LABEL = 'Premium Item';
    const TAG_SELECTOR = '.pb__price-delta-label';
    const INJECTED_PREMIUM_BADGE_SELECTOR = '[data-price-delta-premium-item="true"]';
    // Matches "Add • $14.99", "Add • $14.99", "Add - $11.49", etc.
    const CTA_PRICE_RE = /\$\s*(\d+(?:\.\d{1,2})?)/;

    // Pack discounts rewrite every CTA price after first render — see isAwaitingDiscountedPrice
    let discountConfigured = false;
    let discountPassRan = false;

    function waitForPackBuilder(callback, maxAttempts) {
        const attempts = typeof maxAttempts === 'number' ? maxAttempts : 50;
        if (typeof PackBuilder !== 'undefined') {
            callback();
            return;
        }
        if (attempts <= 0) {
            console.error('[PriceDelta] PackBuilder not found. Load pack-builder.js first.');
            return;
        }
        setTimeout(function () {
            waitForPackBuilder(callback, attempts - 1);
        }, 50);
    }

    function formatMoneyAmount(amount) {
        const rounded = Math.round(Number(amount) * 100) / 100;
        if (Number.isNaN(rounded)) return '0';
        if (Math.abs(rounded - Math.round(rounded)) < EPSILON) {
            return String(Math.round(rounded));
        }
        return rounded.toFixed(2);
    }

    function getBaseline(root) {
        const raw = root && root.dataset ? root.dataset.priceBaseline : null;
        const parsed = parseFloat(raw);
        return Number.isFinite(parsed) ? parsed : DEFAULT_BASELINE;
    }

    /**
     * Highest pack discount configured on the section (6/9/12-pack + additional).
     */
    function maxConfiguredDiscount(root) {
        if (!root || !root.dataset) return 0;
        let max = 0;
        Object.keys(root.dataset).forEach(function (key) {
            if (key.indexOf('iscountPercent-') === -1) return;
            const pct = parseFloat(root.dataset[key]);
            if (Number.isFinite(pct) && pct > max) max = pct;
        });
        return max;
    }

    /**
     * Grid cards render with the undiscounted price; updateDiscountPrice()
     * rewrites every CTA to the discounted price a moment later. The baseline is
     * a post-discount price, so labeling that first value would briefly mark
     * every standard cup "+$2.29 Premium" and badge it. Hold off until the
     * discount pass has run. Quickview CTAs already render discounted, so they
     * fall through immediately.
     */
    function isAwaitingDiscountedPrice(button, price) {
        if (!discountConfigured || discountPassRan || price == null) return false;
        const base = parseFloat(button.dataset.basePrice);
        if (!Number.isFinite(base) || base <= 0) return false;
        return Math.abs(price - base) < EPSILON;
    }

    function parseCtaPrice(button) {
        if (!button) return null;
        const text = (button.textContent || '').trim();
        const match = text.match(CTA_PRICE_RE);
        if (!match) return null;
        const price = Math.round(parseFloat(match[1]) * 100) / 100;
        return Number.isFinite(price) ? price : null;
    }

    /**
     * Prefer the price currently shown in the CTA.
     * Once rewritten to "Add", reuse the last parsed CTA price.
     */
    function getDisplayedCtaPrice(button) {
        const fromText = parseCtaPrice(button);
        if (fromText != null) {
            button.dataset.ctaPrice = String(fromText);
            return fromText;
        }
        const cached = parseFloat(button.dataset.ctaPrice);
        return Number.isFinite(cached) ? Math.round(cached * 100) / 100 : null;
    }

    function buildDeltaMeta(price, baseline) {
        if (price == null || !Number.isFinite(price)) return null;
        const delta = Math.round((price - baseline) * 100) / 100;
        if (!Number.isFinite(delta) || Math.abs(delta) < EPSILON) {
            return null;
        }
        if (delta > 0) {
            return {
                type: 'premium',
                label: '+$' + formatMoneyAmount(delta) + ' Premium',
            };
        }
        return {
            type: 'save',
            label: 'Save $' + formatMoneyAmount(Math.abs(delta)),
        };
    }

    /**
     * Half-cup items (12oz snacks/produce, cup value 0.5) aren't comparable
     * to the full-cup salad baseline — show ½ Cup + displayed price.
     */
    function buildHalfCupMeta(price) {
        const label = price != null && Number.isFinite(price)
            ? '\u00BD Cup \u00B7 $' + formatMoneyAmount(price)
            : '\u00BD Cup';
        return { type: 'half-cup', label: label };
    }

    function isHalfCup(cupValue) {
        const v = Number(cupValue);
        return Number.isFinite(v) && v > 0 && v < 1;
    }

    function isProductAddButton(el) {
        if (!el || el.tagName !== 'BUTTON') return false;
        // Curated-pack CTAs carry quickview-modal__controls-atp too — never tag those
        if (el.classList.contains('pb__curation-add') || el.classList.contains('pb__curation-quickview-add')) {
            return false;
        }
        // Product / quickview add CTAs (with or without a $ still in the label)
        return (
            el.classList.contains('pb__add') ||
            el.classList.contains('pb__product-controls-atp') ||
            el.classList.contains('quickview-modal__controls-atp')
        );
    }

    function hasPremiumItemBadge(root) {
        if (!root) return false;
        const badges = root.querySelectorAll('.pb__product-badge, .quickview-modal__product-badge');
        for (let i = 0; i < badges.length; i++) {
            if ((badges[i].textContent || '').trim().toLowerCase() === PREMIUM_ITEM_LABEL.toLowerCase()) {
                return true;
            }
        }
        return false;
    }

    function getBadgeHost(productEl) {
        if (!productEl) return null;

        let host = productEl.querySelector('.pb__product-badges');
        if (host) return host;

        host = document.createElement('div');
        host.className = 'pb__product-badges';
        productEl.insertBefore(host, productEl.firstChild);
        return host;
    }

    /**
     * Ensure premium-priced cups show the same "Premium Item" image badge
     * already used on products like Steakhouse Wedge (custom.badge metafield).
     * Only inject when missing so we don't duplicate existing metafield badges.
     */
    function syncPremiumItemBadge(productEl, isPremium) {
        if (!productEl) return;

        const existingInjected = productEl.querySelector(INJECTED_PREMIUM_BADGE_SELECTOR);

        if (!isPremium) {
            if (existingInjected) {
                const host = existingInjected.parentElement;
                existingInjected.remove();
                if (host && host.classList.contains('pb__product-badges') && host.children.length === 0) {
                    host.remove();
                }
            }
            return;
        }

        if (hasPremiumItemBadge(productEl)) return;

        const host = getBadgeHost(productEl);
        if (!host) return;

        const tag = document.createElement('span');
        tag.className = 'pb__product-badge';
        tag.setAttribute('data-price-delta-premium-item', 'true');
        tag.textContent = PREMIUM_ITEM_LABEL;
        tag.setAttribute('aria-label', PREMIUM_ITEM_LABEL);
        host.appendChild(tag);
    }

    function syncQuickviewPremiumItemBadge(modalEl, isPremium) {
        if (!modalEl) return;

        const existingInjected = modalEl.querySelector(INJECTED_PREMIUM_BADGE_SELECTOR);

        if (!isPremium) {
            if (existingInjected) {
                const host = existingInjected.parentElement;
                existingInjected.remove();
                if (host && host.classList.contains('quickview-modal__product-badges') && host.children.length === 0) {
                    host.remove();
                }
            }
            return;
        }

        if (hasPremiumItemBadge(modalEl)) return;

        let host = modalEl.querySelector('.quickview-modal__product-badges');
        if (!host) {
            const images = modalEl.querySelector('.quickview-modal__images, .quickview-modal__product-images');
            if (!images) return;
            host = document.createElement('div');
            host.className = 'quickview-modal__product-badges';
            images.insertBefore(host, images.firstChild);
        }

        const tag = document.createElement('span');
        tag.className = 'quickview-modal__product-badge';
        tag.setAttribute('data-price-delta-premium-item', 'true');
        tag.textContent = PREMIUM_ITEM_LABEL;
        host.appendChild(tag);
    }

    /**
     * Inline text label on the macros row (next to protein/carbs).
     * Keeps +$X Premium / Save / ½ Cup separate from the image badge area.
     */
    function syncDeltaLabel(productEl, meta) {
        if (!productEl) return;

        const existing = productEl.querySelector(TAG_SELECTOR);
        if (!meta) {
            if (existing) existing.remove();
            return;
        }

        let tag = existing;
        if (!tag) {
            const host = productEl.querySelector('.pb__product-properties');
            if (!host) return;
            tag = document.createElement('span');
            tag.className = 'pb__price-delta-label';
            host.appendChild(tag);
        }

        tag.textContent = meta.label;
        tag.setAttribute('data-price-delta-type', meta.type);
        tag.setAttribute('aria-label', meta.label);
    }

    function applyToButton(button, baseline) {
        if (!isProductAddButton(button)) return;

        const displayedPrice = getDisplayedCtaPrice(button);
        // No usable CTA price yet — leave alone
        if (displayedPrice == null && !(button.textContent || '').trim().toLowerCase().startsWith('add')) {
            return;
        }

        button.textContent = 'Add';
        button.setAttribute('data-price-delta-applied', 'true');

        // Still showing the pre-discount price — label/badge on the next pass
        if (isAwaitingDiscountedPrice(button, displayedPrice)) return;

        const productEl =
            button.closest('.pb__product') ||
            button.closest('.quickview-modal') ||
            button.closest('.pb__product-container');

        const cupValue = productEl && productEl.dataset ? productEl.dataset.cupValue : null;
        const meta = isHalfCup(cupValue)
            ? buildHalfCupMeta(displayedPrice)
            : buildDeltaMeta(displayedPrice, baseline);
        const isPremium = !!(meta && meta.type === 'premium');

        if (productEl && productEl.classList.contains('quickview-modal')) {
            // Quickview: inline label next to the Add control
            let tag = productEl.querySelector(TAG_SELECTOR);
            if (!meta) {
                if (tag) tag.remove();
                syncQuickviewPremiumItemBadge(productEl, false);
                return;
            }
            if (!tag) {
                tag = document.createElement('span');
                tag.className = 'pb__price-delta-label pb__price-delta-label--quickview';
                button.parentNode.insertBefore(tag, button);
            }
            tag.textContent = meta.label;
            tag.setAttribute('data-price-delta-type', meta.type);
            syncQuickviewPremiumItemBadge(productEl, isPremium);
            return;
        }

        const card =
            productEl && productEl.classList.contains('pb__product')
                ? productEl
                : productEl && productEl.querySelector
                  ? productEl.querySelector('.pb__product')
                  : null;
        syncDeltaLabel(card, meta);
        syncPremiumItemBadge(card, isPremium);
    }

    function applyAll(root) {
        if (!root) return;
        const baseline = getBaseline(root);
        root.querySelectorAll('button.pb__add, button.pb__product-controls-atp').forEach(function (btn) {
            applyToButton(btn, baseline);
        });
        document.querySelectorAll('.quickview-modal button.pb__add, .quickview-modal button.quickview-modal__controls-atp').forEach(function (btn) {
            applyToButton(btn, baseline);
        });
    }

    let bulkRendering = false;
    let refreshScheduled = false;

    function scheduleApplyAll(root) {
        if (bulkRendering || !root) return;
        if (refreshScheduled) return;
        refreshScheduled = true;
        requestAnimationFrame(function () {
            refreshScheduled = false;
            applyAll(root);
        });
    }

    waitForPackBuilder(function () {
        var flagHost = document.querySelector('.pb[data-price-delta-ui="true"]');
        var params = new URLSearchParams(window.location.search);
        var fromQuery = params.get('price_delta') === '1' || params.get('price_delta') === 'true';

        // Shareable preview: ?price_delta=1 on the normal pack-builder product URL
        if (!flagHost && fromQuery) {
            flagHost = document.querySelector('.pb');
            if (flagHost) {
                flagHost.setAttribute('data-price-delta-ui', 'true');
                if (!flagHost.getAttribute('data-price-baseline')) {
                    var fallback =
                        flagHost.getAttribute('data-price-baseline-default') || String(DEFAULT_BASELINE);
                    flagHost.setAttribute('data-price-baseline', fallback);
                }
            }
        }

        if (!flagHost) return;

        const baseline = getBaseline(flagHost);
        discountConfigured = maxConfiguredDiscount(flagHost) > 0;

        const originalUpdateDiscountPrice = PackBuilder.prototype.updateDiscountPrice;
        PackBuilder.prototype.updateDiscountPrice = function () {
            if (typeof originalUpdateDiscountPrice === 'function') {
                originalUpdateDiscountPrice.apply(this, arguments);
            }
            // Discount rewrite puts $ back in the CTA — re-read from that text
            discountPassRan = true;
            applyAll(flagHost);
        };

        const originalRenderProduct = PackBuilder.prototype.renderProduct;
        PackBuilder.prototype.renderProduct = function () {
            const result = originalRenderProduct.apply(this, arguments);
            scheduleApplyAll(flagHost);
            return result;
        };

        const originalOpenQuickview = PackBuilder.prototype.openQuickview;
        if (typeof originalOpenQuickview === 'function') {
            PackBuilder.prototype.openQuickview = function (product) {
                const result = originalOpenQuickview.apply(this, arguments);
                const modalEl = document.querySelector('.quickview-modal');
                if (modalEl) {
                    if (product && product.cup_value != null) {
                        modalEl.dataset.cupValue = String(product.cup_value);
                    } else {
                        delete modalEl.dataset.cupValue;
                    }
                }
                scheduleApplyAll(flagHost);
                return result;
            };
        }

        const qv = document.querySelector('.quickview-modal');
        if (qv) {
            const qvObserver = new MutationObserver(function () {
                scheduleApplyAll(flagHost);
            });
            qvObserver.observe(qv, { childList: true, subtree: true });
        }

        applyAll(flagHost);

        window.__PB_PRICE_DELTA__ = {
            baseline: baseline,
            refresh: function () {
                applyAll(flagHost);
            },
            setBulkRendering: function (isBulk) {
                bulkRendering = Boolean(isBulk);
                if (!bulkRendering) {
                    applyAll(flagHost);
                }
            },
        };
    });
})();
